import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { API_PARAMS } from '../../shared/constants/api.constants';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatError, MatInput } from '@angular/material/input';
import moment from 'moment';
import { DATE_FORMAT } from '../../shared/constants/date.constants';
import { MatDialog } from '@angular/material/dialog';
import { ModifyNameDialogComponent } from '../../shared/components/dialogs/modify-name-dialog/modify-name-dialog.component';
import { DevService } from '../../shared/services/dev.service';
import { SavePhoneDialogComponent } from '../../shared/components/dialogs/save-phone-dialog/save-phone-dialog.component';
import { ModifyEmailDialogComponent } from '../../shared/components/dialogs/modify-email-dialog/modify-email-dialog.component';
import { ModifyInfoService, MemberInfoResponse } from '../../shared/services/modify-info.service';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ModifyPasswordDialogComponent } from '../../shared/components/dialogs/modify-password-dialog/modify-password-dialog.component';
import {
  MatButtonToggle,
  MatButtonToggleGroup,
} from '@angular/material/button-toggle';
import { NgClass, NgOptimizedImage } from '@angular/common';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import SHA256 from 'crypto-js/sha256';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../shared/services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { TextComponent } from '../../shared/components/text/text.component';
import { HealthCheckPlanResponse, QuestionnaireStatusList } from '../../shared/interfaces/my-reservation.model';
import { MyReservationService } from '../../shared/services/my-reservation.service';

/**
 * 會員中心修改基本資料組件
 */
@Component({
  selector: 'app-new-modify-info',
  standalone: true,
  imports: [
    TranslatePipe,
    MatButton,
    MatInput,
    MatIconButton,
    FormsModule,
    ReactiveFormsModule,
    MatError,
    MatButtonToggleGroup,
    MatButtonToggle,
    NgOptimizedImage,
    NgClass,
    TextComponent,
    RouterLink
  ],
  templateUrl: './new-modify-info.component.html',
  styleUrl: './new-modify-info.component.scss',
})
export class NewModifyInfoComponent implements OnInit {
  dialog: MatDialog = inject(MatDialog);
  devService: DevService = inject(DevService);
  modifyInfoService: ModifyInfoService = inject(ModifyInfoService);
  translateService: TranslateService = inject(TranslateService);
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  cdr = inject(ChangeDetectorRef);
  router: Router = inject(Router);
  authService: AuthService = inject(AuthService);
  toastr: ToastrService = inject(ToastrService);
  myReservationService: MyReservationService = inject(MyReservationService);

  gender: string = ''; // 性別
  birthday: string = ''; // 生日(YYYY/MM/DD)
  name: string = ''; // 姓名
  idNumber: string = ''; // 身分證字號
  phone?: string; // 手機
  email: string = ''; // 信箱
  chartNo: string = ''; // 病歷號
  questionnaireStatusList: QuestionnaireStatusList[] = [];
  showPersonalConsentButton: boolean = false; // 新增變數

  currentPasswordControl = new FormControl('', [Validators.required]); // 當前密碼
  isPasswordVisible: boolean = false; // 當前密碼是否可見
  showPasswordError: boolean = false; // 是否顯示當前密碼錯誤訊息
  passwordErrorMessage: string = ''; // 當前密碼錯誤訊息
  isPasswordChangeDisabled: boolean = false; // 是否禁用變更密碼按鈕
  formStatus: string = ''; //個資同意書狀態
  healthCheckPlan?: HealthCheckPlanResponse;

  ngOnInit(): void {
    // TODO 取得登入信息
    this.gender = this.sharedStorageService.get('createMemberRequest')?.gender ?? '';
    this.birthday = moment(this.sharedStorageService.get('createMemberRequest')?.birthday).format(DATE_FORMAT) ?? '';
    this.name = this.sharedStorageService.get('createMemberRequest')?.name ?? '';
    this.idNumber = this.sharedStorageService.get('createMemberRequest')?.idNo ?? '';
    this.phone = this.sharedStorageService.get('createMemberRequest')?.cell ?? '';
    this.email = this.sharedStorageService.get('createMemberRequest')?.email ?? '';
    this.chartNo = this.sharedStorageService.get('createMemberRequest')?.chartNo ?? '';
    // this.checkPasswordChangeLimit();
    this.verifyMemberStatus();
    // 取得同意書狀態
    this.consentStatus();
  }

  // 開啟修改姓名dialog
  openModifyNameDialog() {
    const modifyNameDialog = this.dialog.open(ModifyNameDialogComponent, {
      autoFocus: false,
      data: {
        idNo: this.idNumber,
        name: '',
        chartNo: this.chartNo ?? '', // TODO 病例號
      },
    });

    modifyNameDialog.afterClosed().subscribe((result: string) => {
      if (!result) return; // 如果沒有結果，則不更新
      // 儲存更新後的姓名
      this.name = result;

      // 更新 sharedStorageService 中的資料
      const memberRequest = this.sharedStorageService.get('createMemberRequest');
      if (memberRequest) {
        memberRequest.name = result;
        this.sharedStorageService.set('createMemberRequest', memberRequest);
      }
      modifyNameDialog.close();
    });
  }

  // 開啟新增/修改手機dialog
  openSavePhoneDialog() {
    const savePhoneDialog = this.dialog.open(SavePhoneDialogComponent, {
      autoFocus: false,
      data: {
        isCreate: !this.phone,
        idNo: this.idNumber,
        chartNo: this.chartNo ?? '',
      },
    });

    savePhoneDialog.afterClosed().subscribe((result: string) => {
      if (!result) return; // 如果沒有結果，則不更新
      // 儲存更新後的手機號碼
      this.phone = result;

      // 更新 sharedStorageService 中的資料
      const memberRequest = this.sharedStorageService.get('createMemberRequest');
      if (memberRequest) {
        memberRequest.cell = result;
        this.sharedStorageService.set('createMemberRequest', memberRequest);
      }
      savePhoneDialog.close();
    });
  }

  // 開啟修改信箱dialog
  openModifyEmailDialog() {
    const modifyEmailDialog = this.dialog.open(ModifyEmailDialogComponent, {
      autoFocus: false,
      data: {
        idNo: this.idNumber,
        chartNo: this.chartNo ?? '', // TODO 病例號
        email: this.email,
      },
    });

    modifyEmailDialog.afterClosed().subscribe((result: string) => {
      if (!result) return; // 如果沒有結果，則不更新
      // 儲存更新後的信箱
      this.email = result;

      // 更新 sharedStorageService 中的資料
      const memberRequest = this.sharedStorageService.get('createMemberRequest');
      if (memberRequest) {
        memberRequest.email = result;
        this.sharedStorageService.set('createMemberRequest', memberRequest);
      }
      modifyEmailDialog.close();
    });
  }

  // 切換password可見
  togglePasswordVisibility() {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  // 變更密碼
  modifyPassword() {
    console.log('開啟修改密碼dialog');
    const modifyPasswordDialog = this.dialog.open(ModifyPasswordDialogComponent);
    modifyPasswordDialog.afterClosed().subscribe(result => {
      if (result === 'success') {
        // 成功：顯示成功訊息並登出
        this.showPasswordError = false;
        // localStorage.setItem('lastPasswordChangeDate', new Date().toDateString());
        // this.isPasswordChangeDisabled = true;
        this.currentPasswordControl.setValue('');
        this.logoutAfterPasswordChange();
      } else if (result === 'error') {
        // 失敗：顯示失敗訊息（不登出）
        this.toastr.error(
          this.translateService.instant('modify-password-dialog.modify-error')
        );
      }
      // 如果 result 是 null 或 undefined（使用者取消），則什麼都不做
    });
  }

  // 查詢同意書狀態 (call CB-007)
  private consentStatus(): void {
    this.myReservationService.getHealthCheckPlan(this.chartNo, this.idNumber).subscribe({
      next: (res) => {
        console.log('CB-007 回應:', res);
        this.healthCheckPlan = res.data;
        this.questionnaireStatusList = this.healthCheckPlan?.consentFormData?.questionnaireStatusList ?? [];
        console.log('questionnaireStatusList:', this.questionnaireStatusList);

        // 更新顯示狀態
        const consent = this.questionnaireStatusList.find(item => item.questionnaireTypeId === '5');
        this.showPersonalConsentButton = consent ? consent.status !== '1' : false;
      },
      error: (error) => {
        console.error('查詢同意書狀態失敗:', error);
        this.showPersonalConsentButton = false;
      }
    });
  }

  // 驗證會員狀態 (call CB-009)
  private verifyMemberStatus(): void {
    this.modifyInfoService.getMemberInfo().subscribe({
      next: (res: MemberInfoResponse) => {
        if (res.statusCode === 'EC1001') {
          this.showNotRegisteredAlert();
        }
      },
      error: (error) => console.error('驗證會員狀態失敗:', error)
    });
  }

  private showNotRegisteredAlert(): void {
    const message = this.translateService.instant('member-verify.not-registered-title');

    if (confirm(message)) {
      this.router.navigate(['/healthcheck/login'], {
        queryParams: { tab: '1' }
      });
    }
  }

  /**
   * 變更密碼後登出
   */
  private logoutAfterPasswordChange(): void {
    // 顯示提示訊息
    this.toastr.success(
      this.translateService.instant('modify-password-dialog.success-message')
    );
    // 不打token，直接清除
    // 執行登出（會自動清除 token 和 sharedStorage）
    // this.authService.performLogout().subscribe({
    //   next: (res) => {
    //     this.authService.clearToken();
    //     console.log('登出 API 成功:', res);
    //   },
    //   error: (error) => {
    //     this.authService.clearToken();
    //     console.error('登出 API 失敗:', error);
    //   }
    // });

    // 導向登入頁
    this.sharedStorageService.clear();
    this.sharedStorageService.remove('fromIdLogin');
    this.sharedStorageService.remove('myReservation');
    this.sharedStorageService.remove('savedQuestionnaireData');
    this.authService.clearToken();
    this.router.navigate(['/healthcheck/login']);
  }

  // 修改個人同意書
  modifyPersonalConsent() {
    console.log('修改個人同意書');
    this.sharedStorageService.set('currentRoute', this.router.url);
    this.router.navigate(['/healthcheck/member/mybooking/forms/5']).then();
  }


  protected readonly API_PARAMS = API_PARAMS;
}
